<!-- Floating chrome, over the hero. Present in every state so there is always a way back. -->
<div class="ev-nav">
  <button type="button" class="ev-nav-btn" (click)="back()"
          [attr.aria-label]="'events.back' | translate">
    <app-event-icon name="arrow-back" [size]="20"></app-event-icon>
  </button>

  <button type="button" class="ev-nav-btn" *ngIf="event" (click)="onAddToCalendar()"
          [attr.aria-label]="'events.addToCalendar' | translate">
    <app-event-icon name="calendar-add" [size]="20"></app-event-icon>
  </button>
</div>

<div class="ev-loading" *ngIf="loading" aria-busy="true">
  <div class="ev-loading-hero"></div>
  <div class="ev-loading-bar" style="width: 70%;"></div>
  <div class="ev-loading-bar" style="width: 40%;"></div>
</div>

<div class="ev-state" *ngIf="!loading && notFound">
  <h1 class="ev-state-title">{{ 'events.notFoundTitle' | translate }}</h1>
  <p class="ev-state-text">{{ 'events.notFoundBody' | translate }}</p>
  <button type="button" class="ev-primary-btn" [routerLink]="['/events']">
    {{ 'events.seeAll' | translate }}
  </button>
</div>

<article class="ev-detail" *ngIf="event as e">
  <!-- Hero: image, title and price share one rounded block, as on iOS. -->
  <header class="ev-hero">
    <div class="ev-hero-media">
      <app-event-media class="ev-hero-image" [item]="media" [alt]="e.title || ''"></app-event-media>
      <div class="ev-hero-scrim"></div>

      <div class="ev-hero-caption">
        <h1 class="ev-hero-title">{{ e.title }}</h1>
        <app-event-price-pill class="ev-hero-price" [event]="e" corners="hero"></app-event-price-pill>
      </div>
    </div>

    <div class="ev-hero-facts">
      <app-event-promotion-badge *ngIf="hasPromotion"></app-event-promotion-badge>

      <div class="ev-fact" *ngIf="date">
        <app-event-icon name="calendar" [size]="20"></app-event-icon>
        <span class="ev-fact-strong">{{ date }}</span>
      </div>

      <div class="ev-fact" *ngIf="time">
        <app-event-icon name="clock" [size]="20"></app-event-icon>
        <span>{{ time }}</span>
      </div>
    </div>
  </header>

  <!-- Who it is presented by, where it is, and its actions.
       A CITY event has no venue (D-6), so the identity resolves business → organiser → nothing and
       the section drops entirely when the event has none of them. Rendering it unconditionally is
       what drew an empty avatar over a blank bold line on iOS (I-21); the place and the buttons,
       which a city event DOES have, still show without an identity. -->
  <section class="ev-venue"
           *ngIf="identity || place || address || directionsHref || website || phone">
    <div class="ev-venue-head" *ngIf="identity || place || address">
      <app-event-image class="ev-venue-logo" *ngIf="identity"
                       [url]="identity!.logoUrl" [alt]="identity!.name"></app-event-image>
      <div class="ev-venue-text">
        <!-- Captioned, always: an organiser's name in the venue's slot would otherwise read as the
             venue the diner is sitting in, and we are not the organiser (R-4). -->
        <div class="ev-venue-caption" *ngIf="identity?.isOrganiser">
          {{ 'events.organiser' | translate }}
        </div>
        <div class="ev-venue-name" *ngIf="identity">{{ identity!.name }}</div>
        <div class="ev-venue-line" *ngIf="place">{{ place }}</div>
        <div class="ev-venue-line" *ngIf="address">{{ address }}</div>
      </div>
    </div>

    <div class="ev-venue-actions" *ngIf="directionsHref || website || phone">
      <a class="ev-action" *ngIf="directionsHref" [href]="directionsHref"
         target="_blank" rel="noopener">
        <app-event-icon name="directions" [size]="18"></app-event-icon>
        <span>{{ 'events.directions' | translate }}</span>
      </a>

      <a class="ev-action" *ngIf="website" [href]="website" target="_blank" rel="noopener">
        <app-event-icon name="globe" [size]="18"></app-event-icon>
        <span>{{ 'events.website' | translate }}</span>
      </a>

      <a class="ev-action" *ngIf="phone" [href]="'tel:' + phone">
        <app-event-icon name="phone" [size]="18"></app-event-icon>
        <span>{{ 'events.call' | translate }}</span>
      </a>
    </div>
  </section>

  <!-- About -->
  <section class="ev-about" *ngIf="e.description">
    <h2 class="ev-section-title">{{ 'events.about' | translate }}</h2>

    <!-- Collapsed, the preview has its blank lines squeezed out so the clamp spends every line on
         words; expanded restores the description exactly as it was authored, paragraphs and all. -->
    <p #description class="ev-about-text" [class.ev-clamped]="!descriptionExpanded">
      {{ descriptionExpanded ? e.description : descriptionPreview }}
    </p>

    <button type="button" class="ev-link-btn" *ngIf="descriptionOverflows"
            (click)="toggleDescription()">
      {{ (descriptionExpanded ? 'events.readLess' : 'events.readMore') | translate }}
    </button>
  </section>

  <!-- Related. Empty on every production document today, so this normally never renders. -->
  <section class="ev-related" *ngIf="relatedEvents.length">
    <h2 class="ev-section-title">{{ 'events.related' | translate }}</h2>

    <div class="ev-related-rail">
      <app-event-vertical-card class="ev-related-item"
        *ngFor="let related of relatedEvents; trackBy: trackById"
        [event]="related"
        [showBusinessInfo]="true"
        (openEvent)="onOpenRelated($event)"
        (addToCalendar)="onAddToCalendar()">
      </app-event-vertical-card>
    </div>
  </section>

  <!-- Spacer so the last content clears the sticky footer. -->
  <div class="ev-footer-spacer" *ngIf="bookKind"></div>
</article>

<!-- Book now -->
<div class="ev-footer" *ngIf="event && bookKind">
  <button type="button" class="ev-book-btn" (click)="onBook()">{{ bookLabel }}</button>
</div>

<div class="ev-toast" *ngIf="toast" role="status">{{ toast }}</div>
